// @ts-nocheck

import eventBus from "./event-bus.module.ts";
import { commonState, updateCommonState } from "./common-state.module.ts";

let addToCartElements: NodeListOf<HTMLElement> | null = null;
const cachedRelatedElements = new Map();
const cachedRelatedInputs = new Map();

/**
 * Получить data аттрибуты количества товара у элемента
 * и функции для их обработки
 * @param {HTMLElement} element
 * @returns
 */
export function getQuantityAttributes(element) {
    /**
     * @type {NodeListOf<HTMLElement>}
     */
    if (addToCartElements === null) {
        addToCartElements = Array.from(
            document.querySelectorAll("[data-handle-add-to-cart]"),
        );
    }

    const productId = parseInt(element.dataset.productId || "");
    const variantId = parseInt(element.dataset.variantId || "");

    const cacheKey = productId.toString() + variantId.toString();
    if (!cachedRelatedElements.has(cacheKey)) {
        cachedRelatedElements.set(
            cacheKey,
            addToCartElements.filter((e) => {
                const pId = parseInt(e.dataset.productId || "");
                const vId = parseInt(e.dataset.variantId || "");
                return pId === productId && vId === variantId;
            }),
        );
    }

    // Список всех элементов, обрабатывающих изменение количества того же варианта продукта
    const relatedElements = cachedRelatedElements.get(cacheKey);

    if (!cachedRelatedInputs.has(cacheKey)) {
        cachedRelatedInputs.set(
            cacheKey,
            relatedElements.filter((e) => e instanceof HTMLInputElement),
        );
    }

    const relatedInputs =
        /** @type {HTMLInputElement[]} */ cachedRelatedInputs.get(cacheKey);

    let quantityMin = parseInt(element.dataset.quantityMin || "");
    if (Number.isNaN(quantityMin)) {
        quantityMin = 0;
    }
    quantityMin = Math.max(quantityMin, 0);

    let quantityMax = parseInt(element.dataset.quantityInStock || "");
    if (Number.isNaN(quantityMax)) {
        quantityMax = Infinity;
    }

    let quantityPrev = parseInt(element.dataset.quantity || "");
    if (Number.isNaN(quantityPrev)) {
        quantityPrev = quantityMin;
    }

    let amountChange = 0;
    if ("increase" in element.dataset) {
        amountChange = 1;
    } else if ("decrease" in element.dataset) {
        amountChange = -1;
    }

    /**
     * Установить новое значение количества и отправить запрос
     * @param {number} quantity
     * @param {boolean} [updateInputValue=true]
     */
    function setQuantity(quantity, updateInputValue = true) {
        if (quantity < quantityMin) {
            return;
        }
        if (quantity > quantityMax) {
            if (quantityPrev < quantityMax) {
                quantity = quantityMax;
                eventBus.publish("not-enough-products", {
                    message: commonState.translations[
                        "Only {quantity} pieces available"
                    ].replace("{quantity}", quantityMax),
                });
            } else {
                eventBus.publish("not-enough-products", {
                    message:
                        commonState.translations[
                            "You have added the maximum quantity of this product"
                        ],
                });
                return;
            }
        }
        if (quantity !== quantityPrev) {
            relatedElements.forEach((e) => {
                e.dataset.quantity = quantity.toString();
                if (e instanceof HTMLButtonElement) {
                    if ("increase" in e.dataset) {
                        e.disabled = quantity >= quantityMax;
                    } else if ("decrease" in e.dataset) {
                        e.disabled = quantity <= quantityMin;
                    }
                }
            });
            updateVariantInCart({
                productId,
                variantId,
                quantity,
                quantityPrev,
            });
        }
        if (updateInputValue) {
            relatedInputs.forEach((i) => {
                i.value = quantity.toString();
            });
        }
    }

    /**
     * Восстановить последнее корректно значение количества для элемента input
     */
    function resetInputValue() {
        relatedInputs.forEach((i) => {
            i.value = quantityPrev.toString();
        });
    }

    /**
     * Ограничить количество товара минимальным и максимальным значением
     * @param {number} quantity
     */
    function clamp(quantity) {
        return Math.min(Math.max(quantity, quantityMin), quantityMax);
    }

    return {
        quantity: quantityPrev,
        quantityMin,
        quantityMax,
        amountChange,
        setQuantity,
        resetInputValue,
        clamp,
    };
}

/**
 * Запрос изменения количества товаров в корзине
 * @typedef {Object} CartRequest
 * @property {number} productId ID продукта
 * @property {number} variantId ID варианта
 * @property {number} quantity Новое количество
 * @property {number} quantityPrev Предыдущее количество
 */

/**
 * Очередь запросов изменения количества товаров в корзине
 * @type {CartRequest[]}
 */
const cartRequestsQueue = [];
// Происходит ли запрос изменения корзины
let isLoading = false;
// Промис для ожидания остановки запросов корзины
let stopPromise = null;
let stopPromiseResolve = null;

/**
 * Изменение количества варианта продукта в корзине.
 * Запросы на изменение добавляются в очередь и выполняются последовательно.
 * Если в очереди уже есть запрос на изменение количества данного варианта,
 * то заменить его на новый, иначе добавить запрос в очередь.
 * @param {CartRequest} request
 */
function updateVariantInCart(request) {
    if (stopPromise) {
        return;
    }
    if (isLoading) {
        const oldRequest = cartRequestsQueue.find(
            (r) =>
                r.productId === request.productId &&
                r.variantId === request.variantId,
        );
        if (oldRequest) {
            oldRequest.quantity = request.quantity;
        } else {
            cartRequestsQueue.push(request);
        }
    } else {
        isLoading = true;
        cartRequestsQueue.push(request);
        setTimeout(async () => {
            while (cartRequestsQueue.length > 0) {
                const r = /** @type {CartRequest} */ cartRequestsQueue.shift();
                await updateVariantInCartRecursively(r, 5);
            }
            isLoading = false;
            if (stopPromiseResolve) {
                stopPromiseResolve();
            }
        }, 50);
    }
}

/**
 * Повторять запросы несколько раз, если версия корзины не актуальна
 * @param {CartRequest} request
 * @param {number} depth
 */
function updateVariantInCartRecursively(request, depth) {
    return fetch("/cart/update", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            Version: commonState.cart.version,
            Id: request.productId,
            VariantId: request.variantId,
            Quantity: request.quantity,
        }),
    })
        .then((response) => {
            return handleCartResponse(response);
        })
        .then((data) => {
            updateCommonState({
                cart: {
                    version: data.cart.version,
                    code: data.cart.code,
                },
            });
            eventBus.publish("cart-changed", {
                ...request,
                total: data.totalQuantityItems,
                totalPrice: data.cart.total,
                discountedTotal: data.cart.discountedTotal,
            });
        })
        .catch((error) => {
            if (
                error instanceof HTTPError &&
                error.data.statusCode === 409 &&
                depth > 1
            ) {
                return getCart().then(() => {
                    return updateVariantInCartRecursively(request, depth - 1);
                });
            }
            eventBus.publish("update-cart-error", { error });
            throw error;
        });
}

// Получить новую версию корзины
function getCart() {
    return fetch("/cart/get", { method: "GET" })
        .then((response) => {
            return handleCartResponse(response);
        })
        .then((data) => {
            updateCommonState({
                cart: {
                    version: data.cart.version,
                    code: data.cart.code,
                },
            });
        });
}

function handleCartResponse(response) {
    if (response.ok) {
        return response.json();
    }
    return response.json().then((data) => {
        throw new HTTPError(data);
    });
}

class HTTPError extends Error {
    constructor(data) {
        super(data.errorMessage || "Failed to connect");
        this.data = data;
    }
}

/**
 * Остановить возможность добавления новых запросов в очередь
 * Дождаться завершения всех текущих запросов
 */
export async function stopCartRequests() {
    if (!stopPromise) {
        stopPromise = new Promise((resolve) => {
            stopPromiseResolve = resolve;
        });
    }
    if (!isLoading) {
        stopPromiseResolve();
    }
    return stopPromise;
}

/**
 * Возобновить возможность добавления новых запросов в очередь
 * Если идет процесс остановки, то дождаться его завершения
 */
export async function resetCartRequests() {
    if (stopPromise) {
        await stopPromise;
    }
    stopPromise = null;
    stopPromiseResolve = null;
}
